import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'

// Layouts
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import AdminLayout from '@/layouts/AdminLayout.vue'


// Views - Cliente
import HomeView from '@/views/HomeView.vue'
import MenuView from '@/views/MenuView.vue'
import ProductDetailView from '@/views/ProductDetailView.vue'
import CartView from '@/views/CartView.vue'
import CheckoutView from '@/views/CheckoutView.vue'
import OrderConfirmationView from '@/views/OrderConfirmationView.vue'
import OrderTrackView from '@/views/OrderTrackView.vue'

// Views - Auth
import ForgotPasswordView from '@/views/ForgotPasswordView.vue'
import ResetPasswordView from '@/views/ResetPasswordView.vue'

// Views - Driver
import DriverLoginView from '@/views/DriverLoginView.vue'
import DriverOrdersView from '@/views/DriverOrdersView.vue'

// Views - Admin
import LoginView from '@/views/admin/LoginView.vue'
import DashboardView from '@/views/admin/DashboardView.vue'
import OrdersManageView from '@/views/admin/OrdersManageView.vue'
import ProductsManageView from '@/views/admin/ProductsManageView.vue'
import CategoriesManageView from '@/views/admin/CategoriesManageView.vue'
import CustomersManageView from '@/views/admin/CustomersManageView.vue'
import DeliverySettingsView from '@/views/admin/DeliverySettingsView.vue'
import DriversManageView from '@/views/admin/DriversManageView.vue'
import CompanySettingsView from '@/views/admin/CompanySettingsView.vue'
import ReportsView from '@/views/admin/ReportsView.vue'
import SuperAdminBusinessPlansView from '@/views/superadmin/SuperAdminBusinessPlansView.vue'
import SuperAdminOrdersReportView from '@/views/superadmin/SuperAdminOrdersReportView.vue'

// Views - SuperAdmin
import SuperAdminLoginView from '@/views/superadmin/SuperAdminLoginView.vue'
import SuperAdminLayout from '@/views/superadmin/SuperAdminLayout.vue'
import CompaniesListView from '@/views/superadmin/CompaniesListView.vue'
import UsersManageView from '@/views/superadmin/UsersManageView.vue'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/',
      component: DefaultLayout,
      children: [
        {
          path: '',
          name: 'home',
          component: HomeView,
          meta: { title: 'Início' }
        },
        {
          path: 'menu',
          name: 'menu',
          component: MenuView,
          meta: { title: 'Cardápio' }
        },
        {
          path: 'product/:id',
          name: 'product-detail',
          component: ProductDetailView,
          meta: { title: 'Detalhes do Produto' }
        },
        {
          path: 'cart',
          name: 'cart',
          component: CartView,
          meta: { title: 'Carrinho' }
        },
        {
          path: 'checkout',
          name: 'checkout',
          component: CheckoutView,
          meta: { title: 'Finalizar Pedido', requiresCart: true }
        },
        {
          path: 'order/confirmation/:orderNumber',
          name: 'order-confirmation',
          component: OrderConfirmationView,
          meta: { title: 'Pedido Confirmado' }
        },
        {
          path: 'order/track',
          name: 'order-track',
          component: OrderTrackView,
          meta: { title: 'Rastrear Pedido' }
        }
      ]
    },
    {
      path: '/login',
      name: 'admin-login',
      component: LoginView,
      meta: { title: 'Login Administrativo', guestOnly: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordView,
      meta: { title: 'Recuperar Senha', guestOnly: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPasswordView,
      meta: { title: 'Nova Senha', guestOnly: true }
    },
    {
      path: '/admin',
      component: AdminLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'admin-dashboard',
          component: DashboardView,
          meta: { title: 'Dashboard' }
        },
        {
          path: 'orders',
          name: 'admin-orders',
          component: OrdersManageView,
          meta: { title: 'Gerenciar Pedidos' }
        },
        {
          path: 'products',
          name: 'admin-products',
          component: ProductsManageView,
          meta: { title: 'Gerenciar Produtos' }
        },
        {
          path: 'categories',
          name: 'admin-categories',
          component: CategoriesManageView,
          meta: { title: 'Gerenciar Categorias' }
        },
        {
          path: 'customers',
          name: 'admin-customers',
          component: CustomersManageView,
          meta: { title: 'Clientes' }
        },
        {
          path: 'delivery',
          name: 'admin-delivery',
          component: DeliverySettingsView,
          meta: { title: 'Configurações de Entrega' }
        },
        {
          path: 'drivers',
          name: 'admin-drivers',
          component: DriversManageView,
          meta: { title: 'Entregadores' }
        },
        {
          path: 'settings',
          name: 'admin-settings',
          component: CompanySettingsView,
          meta: { title: 'Configurações' }
        },
        {
          path: 'reports/sales',
          name: 'admin-reports-sales',
          component: ReportsView,
          meta: { title: 'Relatório de Vendas' }
        },
        {
          path: 'reports/deliveries',
          name: 'admin-reports-deliveries',
          component: ReportsView,
          meta: { title: 'Relatório de Entregas' }
        }
      ]
    },
    // SuperAdmin Routes
    {
      path: '/superadmin/login',
      name: 'superadmin-login',
      component: SuperAdminLoginView,
      meta: { title: 'Super Admin Login', guestOnly: true }
    },
    {
      path: '/superadmin',
      component: SuperAdminLayout,
      meta: { requiresSuperAdmin: true },
      children: [
        {
          path: '',
          name: 'superadmin-companies',
          component: CompaniesListView,
          meta: { title: 'Gerenciar Empresas' }
        },
        {
          path: 'users',
          name: 'superadmin-users',
          component: UsersManageView,
          meta: { title: 'Gerenciar Usuários' }
        },
        {
          path: 'business-plans',
          name: 'superadmin-business-plans',
          component: SuperAdminBusinessPlansView,
          meta: { title: 'Planos de Negócios' }
        },
        {
          path: 'orders-report',
          name: 'superadmin-orders-report',
          component: SuperAdminOrdersReportView,
          meta: { title: 'Pedidos por Cliente' }
        }
      ]
    },
    {
      path: '/driver/login',
      name: 'driver-login',
      component: DriverLoginView,
      meta: { title: 'Login do Entregador' }
    },
    {
      path: '/driver/orders',
      name: 'driver-orders',
      component: DriverOrdersView,
      meta: { title: 'Pedidos do Entregador' }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { title: 'Página não encontrada' }
    }
  ],
  scrollBehavior() {
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()
  
  // Inicializa auth
  authStore.init()
  
  // Carrega empresa correta baseada no hostname
  const isAdminRoute = to.path.startsWith('/admin') || to.path.startsWith('/login') || to.path.startsWith('/superadmin')
  if (!isAdminRoute) {
    const hostname = companyStore.detectCompanyByHostname()
    
    if (hostname) {
      // Se há hostname detectado, força carregamento pela URL
      if (companyStore.isLoaded && companyStore.company?.custom_domain !== hostname) {
        const base = companyStore.company?.custom_domain_base || companyStore.company?.domain_base
        const expected = companyStore.company?.subdomain && base
          ? `${companyStore.company.subdomain}.${base}`
          : companyStore.company?.custom_domain
        if (expected !== hostname) {
          companyStore.clear()
        }
      }
      if (!companyStore.isLoaded) {
        try {
          await companyStore.fetchByHostname(hostname)
        } catch (error) {
          console.warn('Não foi possível carregar empresa pelo hostname:', error)
        }
      }
    } else {
      // Sem hostname reconhecido (localhost/IP): fallback para localStorage
      companyStore.init()
      if (!companyStore.isLoaded) {
        const companySlug = localStorage.getItem('company_slug')
        const companyId = localStorage.getItem('company_id')
        try {
          if (companySlug) {
            await companyStore.fetchBySlug(companySlug)
          } else if (companyId) {
            await companyStore.fetchById(parseInt(companyId))
          }
        } catch (error) {
          console.warn('Não foi possível carregar dados da empresa:', error)
        }
      }
    }
  }
  
  // Atualiza título da página
  if (to.meta.title) {
    document.title = `${to.meta.title} - Delivery SaaS`
  }
  
  // Verifica se requer autenticação
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next({ name: 'admin-login', query: { redirect: to.fullPath } })
  }
  
  // Verifica se requer super admin
  if (to.meta.requiresSuperAdmin) {
    if (!authStore.isAuthenticated) {
      return next({ name: 'superadmin-login', query: { redirect: to.fullPath } })
    }
    if (!authStore.user?.is_superuser && authStore.user?.role !== 'super_admin') {
      return next({ name: 'home' })
    }
  }
  
  // Verifica se é apenas para visitantes (não logados)
  // Ignora para rotas de superadmin
  if (to.meta.guestOnly && authStore.isAuthenticated && !to.path.startsWith('/superadmin')) {
    return next({ name: 'admin-dashboard' })
  }
  
  // Verifica se requer itens no carrinho
  if (to.meta.requiresCart) {
    const cartData = localStorage.getItem('cart')
    if (!cartData) {
      return next({ name: 'cart' })
    }
    try {
      const cart = JSON.parse(cartData)
      if (!cart.items || cart.items.length === 0) {
        return next({ name: 'cart' })
      }
    } catch {
      return next({ name: 'cart' })
    }
  }
  
  next()
})

export default router
